import { runInAction, makeAutoObservable, reaction, comparer } from 'mobx';
import {
    get,
    patch,
    post,
    remove,
    requests,
    is2dFile,
    is3dFile,
    isStlFile,
    restricted_file_extensions,
    STL_MANUFACTURING_TYPE,
} from '@partsbadger/utils';
import { matchPartFiles } from '@partsbadger/one-true-form/lib/utils/partMatching';

import materialStore from './MaterialStore';
import { trackLuckyOrangeEvent } from '../utils/luckyOrange';
import { buildManufacturingTypePayloadForStlPart } from '../utils/stlManufacturing';
import { IPartFileRecord } from '@partsbadger/types';
import { QuoteProps, QuoteLineItemProps } from '../types/types';
import _ from 'lodash';

import { TablePaginationConfig } from 'antd/es/table';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import {
    buildPendingEmbeddedExtraction,
    dequeueEmbeddedExtraction,
    enqueueEmbeddedExtraction,
    PendingEmbeddedExtraction,
} from '@partsbadger/library';

import CoatingStore from '../stores/CoatingStore';
import MaterialStore from './MaterialStore';

interface ErrorField {
    step: number;
    fieldName: string;
    lineItem: string;
}

export type BackgroundUploadStatus = 'queued' | 'uploading' | 'completed' | 'failed';

export interface BackgroundUploadJob {
    uid: string;
    item: IPartFileRecord;
    position: number;
    label: string;
    status: BackgroundUploadStatus;
    progress: number;
    error?: string;
    /** The quote the part is added to, fixed when queued: opening another quote meanwhile must not move it. */
    quoteUid: string | null;
    email?: string | null;
    /** Set once the part exists on the server, so a retry does not create it twice. */
    lineItemId?: number;
}

/** What to tell the user when an upload failed. */
export const describeUploadError = (error: unknown): string => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 413) return 'File too large';
    if (status === 404) return 'This quote is no longer available';
    if (status === 403 || status === 401) return 'You no longer have access to this quote';
    return (error instanceof Error && error.message) || 'Upload failed';
};

/** Response from POST /customer/quotes/:id/new-revision/ */
export type QuoteNewRevisionResponse = {
    id: number;
    name: string;
};

export interface FileProps {
    index: number;
    uid: string;
    name: string;
    file: File;
    // '3D File': {
    //     uid: string;
    // };
    // '2D File': {
    //     uid: string;
    // };
    // 'Additional File': Array<AdditionalFile>;
    type: '3D File' | '2D File' | 'Additional File';
    similarity: number;
    uploading: boolean;
    progress: number;
}

export class QuoteStore {
    loading = false;
    quoteLoading = false;
    quoteSummaryLoading = false;
    loadingLineItemPartId: number | null = null;
    quote: QuoteProps | null = null;
    quotes: QuoteProps[] = [];
    pagination: TablePaginationConfig = { pageSize: 20 };
    line_items: QuoteLineItemProps[] = [];
    line_item: QuoteLineItemProps | undefined;
    parent_line_items: any;
    application_cost = 0;
    show_prices = true;
    selectedLineItems: QuoteLineItemProps[] = [];
    fileParts: Array<IPartFileRecord> = [];
    quoteUid: string | null = null;
    isConnectedToSocket = false;
    expandedPartId: number | null = null;
    rapidQuoteUid?: string | null = null;
    pendingEmbeddedExtractionQueue: PendingEmbeddedExtraction[] = [];
    extractEmbeddedLoading = false;
    backgroundUploadJobs: BackgroundUploadJob[] = [];

    /** Parts of the batch that are queued or being uploaded. */
    get pendingUploadCount() {
        return this.backgroundUploadJobs.filter(job => job.status === 'queued' || job.status === 'uploading').length;
    }

    /**
     * A batch of several parts is uploading and none has been added yet: the list is empty or has
     * not changed, and the user cannot tell the files were taken. One part appears right away.
     */
    get awaitingFirstPart() {
        return this.pendingUploadCount >= 2 && !this.backgroundUploadJobs.some(job => job.status === 'completed');
    }
    backgroundUploadRunning = false;
    backgroundUploadDismissed = false;
    uploadProgressUpdatedAt = new Map<string, number>();
    backgroundUploadExecutor:
        | ((item: IPartFileRecord, position: number, job?: BackgroundUploadJob) => Promise<void>)
        | null = null;

    constructor() {
        makeAutoObservable(
            this,
            { uploadProgressUpdatedAt: false, backgroundUploadExecutor: false },
            { autoBind: true }
        );

        // reaction(
        //     () =>
        //         this.line_items.map(item => ({
        //             lineItemId: item.id,
        //             materialId: item.product?.material_related?.id,
        //         })) ?? [],
        //     async materials => {
        //         if (!materials.length) return;

        //         await CoatingStore.syncCoatingsForQuote(this.line_items);

        //         runInAction(() => {
        //             this.validateLineItemCoatings();
        //         });
        //     },
        //     {
        //         equals: comparer.structural,
        //         // Debounce rapid quote mutations (e.g. multi-file upload creating many line items)
        //         // so we only sync coatings once per burst of changes.
        //         delay: 250,
        //     }
        // );

        const token = localStorage.getItem('quote_token');
        if (token) {
            this.setRapidQuoteUid(token);
        }
    }

    get getExpandedPartId() {
        return this.expandedPartId;
    }

    get pendingEmbeddedExtraction(): PendingEmbeddedExtraction | null {
        return this.pendingEmbeddedExtractionQueue[0] ?? null;
    }

    get pendingEmbeddedExtractionQueueSize(): number {
        return this.pendingEmbeddedExtractionQueue.length;
    }

    maybeQueueEmbeddedExtraction(lineItem: QuoteLineItemProps, partLabel?: string) {
        const payload = lineItem.embedded_pdf_attachments;
        if (!payload?.attachments?.length) {
            return;
        }

        const pending = buildPendingEmbeddedExtraction(
            lineItem.id,
            payload.drawing_file_id,
            payload.attachments,
            partLabel || lineItem.name
        );

        if (pending) {
            this.enqueueEmbeddedExtraction(pending);
        }
    }

    enqueueEmbeddedExtraction(item: PendingEmbeddedExtraction) {
        this.pendingEmbeddedExtractionQueue = enqueueEmbeddedExtraction(this.pendingEmbeddedExtractionQueue, item);
    }

    clearPendingEmbeddedExtraction() {
        this.pendingEmbeddedExtractionQueue = dequeueEmbeddedExtraction(this.pendingEmbeddedExtractionQueue);
    }

    async confirmExtractEmbedded3d(filenames: string[]) {
        const pending = this.pendingEmbeddedExtraction;
        if (!pending) {
            return;
        }

        this.extractEmbeddedLoading = true;

        try {
            await this.extractEmbedded3d({
                line_item_id: pending.lineItemId,
                drawing_file_id: pending.drawingFileId,
                filenames,
            });
            this.clearPendingEmbeddedExtraction();
        } finally {
            this.extractEmbeddedLoading = false;
        }
    }

    async extractEmbedded3d(payload: { line_item_id: number; drawing_file_id: number; filenames: string[] }) {
        const quoteUid = await this.requireQuoteUid();

        return post(`/customer/quote/${quoteUid}/extract-embedded-3d/`, payload)
            .then(async data => {
                await this.setQuoteLineItem(data);
                this.maybeQueueEmbeddedExtraction(data);
                return data;
            })
            .catch(err => {
                message.error('Could not extract embedded 3D files');
                throw err;
            });
    }

    async validateLineItemCoatings() {
        if (!this.quote) return;

        this.quote.line_items.forEach(async item => {
            const materialId = item.product?.material_related?.id;
            if (!materialId) return;

            const coatingsForMaterial = CoatingStore.coatingsByMaterial.get(materialId) ?? [];

            const isCompatible = coatingsForMaterial.some(coating => coating.id === item.coating?.id);

            if (!isCompatible) {
                const customCoating = coatingsForMaterial.find(coating => coating.name === 'Custom');
                if (customCoating) {
                    await this.updateQuoteLineItem({
                        id: item.id,
                        product: { coatings: [customCoating?.id] },
                    });
                }
            }
        });
    }

    setQuoteSummaryLoading(value: boolean) {
        this.quoteSummaryLoading = value;
    }

    setQuoteLoading(value: boolean) {
        this.quoteLoading = value;
    }

    setRapidQuoteUid(signedId: string | null) {
        this.rapidQuoteUid = signedId;
    }

    getRapidQuote(payload: unknown) {
        if (payload) {
            return post(`/customer/quote/start/`, payload).then(data => {
                runInAction(() => {
                    window.localStorage.setItem('quote_token', data.quote_token);
                    this.setRapidQuoteUid(data.quote_token);
                    this.quote = data.quote;
                });

                return data;
            });
        }
    }

    setExpandedPartId(id: number | null) {
        this.expandedPartId = id;
    }

    setSocketStatus(connected: boolean) {
        this.isConnectedToSocket = connected;
    }

    setLoading(loading: boolean) {
        runInAction(() => {
            this.loading = loading;
        });
    }

    setPagination(pagination: TablePaginationConfig) {
        runInAction(() => {
            this.pagination = pagination;
        });
    }

    duplicateItem(master_product: number) {
        return requests
            .post(`/customer/quote/${this.getQuoteUid}/duplicate-item/${master_product}/`)
            .then(data => {
                runInAction(() => {
                    if (this.getQuoteUid) {
                        this.line_items.push(data);
                    }
                });
            })
            .catch(err => {
                console.log(err);
                message.error('Error, please try again');
            });
    }

    async duplicateMasterProduct(master_product_id: number, duplicated_reason: string): Promise<void> {
        const quoteUid = this.getQuoteUid;

        if (!quoteUid) {
            message.error('Error, please try again');
            return;
        }

        await post(`/customer/quote/${quoteUid}/duplicate-master-product/`, {
            master_product: master_product_id,
            duplicated_reason,
        });

        await this.getQuoteByUid(quoteUid);
    }

    async getLineItems(quote_item_id: number) {
        const response = await get(`/customer/quotes/${quote_item_id}/line-items/`);
        runInAction(() => {
            this.line_items = response.results;
            this.line_items.map(item => {
                if (item.has_dynamic_quantities) {
                    const dynamics = item.quote_item_quantities.filter(
                        (dynamic: { displayed: boolean }) => dynamic.displayed
                    );
                    if (dynamics.length === 0) {
                        this.line_items.splice(
                            this.line_items.findIndex((_item: { id: number }) => _item.id === item.id),
                            1
                        );
                    }
                }
            });
        });
    }

    patchQuote(quote_id: number, payload: any) {
        return patch(`/customer/quotes/${quote_id}/`, payload);
    }

    sendSalesOrder(payload: any) {
        return post(`/customer/sales-orders/`, payload);
    }

    getDynamicPricesList(quote_id: number, quote_item_id: number, page: number) {
        return get(`/customer/quotes/${quote_id}/all-dynamic-quantities/${quote_item_id}/?page=${page}`);
    }

    addDynamicVariation(quote_id: number, quote_item_id: number, quantity: number) {
        return post(`/customer/quotes/${quote_id}/add_quantity/${quote_item_id}/`, {
            quantity: quantity,
        });
    }
    deleteLineItem(quote_id: number, payload: any) {
        return post(`/customer/quotes/${quote_id}/delete-line-item/`, payload);
    }

    deleteDynamicVariation(quote_id: number, dynamic_id: number) {
        return remove(`/customer/quotes/${quote_id}/delete-quote-item-quantity/${dynamic_id}`);
    }

    duplicateItemForRequote(quote_id: number, payload: any) {
        return post(`/customer/quotes/${quote_id}/duplicate_item_for_requote/`, payload);
    }

    async getPriceByQuantity(quote_id: number, quote_item_id: number, quantity: number) {
        return await post(`/customer/quotes/${quote_id}/get-price-by-quantity/`, {
            quantity: quantity,
            quote_has_product: quote_item_id,
        });
    }

    async getQuoteById(quote_id: number) {
        const res = await get(`/customer/quotes/${quote_id}/`);

        runInAction(() => {
            this.quote = res;
        });

        return res;
    }

    /**
     * @deprecated The method should not be used, instead use getQuotes
     */
    searchQuotes(value_search: string) {
        return get(`/customer/quotes/?stage=Completed&&search=${value_search}`);
    }

    async getQuotesPreview(params: Record<string, unknown>) {
        const response = await get(`/customer/quotes/`, {
            params: params,
        });

        return response.results;
    }

    async getQuotes(params: Record<string, unknown>) {
        runInAction(() => {
            this.loading = true;
        });

        const r = await get(`/customer/quotes/`, {
            params: params,
        }).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });
        runInAction(() => {
            this.quotes = r.results;
            this.pagination.total = r.count;
        });

        return r.results;
    }

    updateLineItem(id: number, payload = {}) {
        return patch(`/customer/quotes/${id}/update-item/`, payload).then(data => {
            runInAction(() => {
                if (!this.quote || !data?.quote) return;

                this.quote = data.quote;
                // update only line items
                data.quote.line_items.forEach((updatedItem: any) => {
                    const existingItem = this.line_items.find(item => item.id === updatedItem.id);

                    if (existingItem) {
                        Object.assign(existingItem, updatedItem);
                    }
                });
            });

            return data;
        });
    }

    setLineItemLoading(value: number | null) {
        runInAction(() => {
            this.loadingLineItemPartId = value;
        });
    }

    refreshMasterProductGroupFromQuote(masterProductId: number, quoteData: QuoteProps) {
        if (!quoteData?.line_items?.length) return;

        runInAction(() => {
            const refreshedGroup = quoteData.line_items.filter(
                item => item?.product?.master_product === masterProductId
            );

            if (!refreshedGroup.length) return;

            const refreshedById = new Map(refreshedGroup.map(item => [item.id, item]));

            this.line_items = this.line_items.map(item => {
                const refreshed = refreshedById.get(item.id);
                return refreshed ? refreshed : item;
            });

            if (this.quote) {
                this.quote = {
                    ...this.quote,
                    ...quoteData,
                    line_items: this.line_items,
                };
            }
        });
    }

    getLineItemById(id: number) {
        return this.line_items.find(item => item.id === id) ?? null;
    }

    getSiblingVariations(lineItemId: number, masterProductId: number) {
        return this.line_items.filter(
            item => item.product?.master_product === masterProductId && item.id !== lineItemId
        );
    }

    getFallbackMaterialIdsByManufacturingType(manufacturingType?: string): number[] {
        if (!manufacturingType) return [];

        const validMaterials = MaterialStore.materials.filter(
            material =>
                Array.isArray(material.manufacturing_types) &&
                material.manufacturing_types.some(type => type.toLowerCase() === manufacturingType.toLowerCase())
        );

        return validMaterials.map(material => material.id).filter((id): id is number => typeof id === 'number');
    }

    getTwoFallbackMaterialIds(
        manufacturingType?: string,
        currentMaterialId?: number | null
    ): [number | null, number | null] {
        const ids = this.getFallbackMaterialIdsByManufacturingType(manufacturingType);

        if (!ids.length) return [null, null];

        const distinct = [...new Set(ids)];

        const firstNonCurrent = distinct.find(id => id !== currentMaterialId) ?? null;
        const secondNonCurrent = distinct.find(id => id !== currentMaterialId && id !== firstNonCurrent) ?? null;

        return [firstNonCurrent, secondNonCurrent];
    }

    getResetMaterialIdForLineItem(lineItem: QuoteLineItemProps): number | null {
        const currentMaterialId = lineItem.product?.material_related?.id ?? null;
        const manufacturingType = lineItem.manufacturing_type;

        const [firstFallback, secondFallback] = this.getTwoFallbackMaterialIds(manufacturingType, currentMaterialId);

        return firstFallback ?? secondFallback ?? null;
    }

    async repriceSiblingVariationsAfter2dDelete(lineItemId: number, masterProductId: number) {
        const siblings = this.line_items.filter(
            item =>
                item.id !== lineItemId &&
                item.product?.master_product === masterProductId &&
                item.manufacturing_type === 'CNC'
        );

        for (const sibling of siblings) {
            const currentMaterialId = sibling.product?.material_related?.id ?? null;

            if (!currentMaterialId) continue;

            const resetMaterialId = this.getResetMaterialIdForLineItem(sibling);

            if (!resetMaterialId || resetMaterialId === currentMaterialId) continue;

            await patch(`/customer/quote/${this.getQuoteUid}/update-item/`, {
                id: sibling.id,
                product: {
                    material: resetMaterialId,
                },
            }).then(data => {
                this.setQuoteLineItem(data);
            });

            await patch(`/customer/quote/${this.getQuoteUid}/update-item/`, {
                id: sibling.id,
                product: {
                    material: currentMaterialId,
                },
            }).then(data => {
                this.setQuoteLineItem(data);
            });
        }

        if (this.getQuoteUid) {
            const freshQuote = await this.getQuoteByUid(this.getQuoteUid, {
                updateLineItems: true,
                useSummaryLoading: true,
            });

            this.refreshMasterProductGroupFromQuote(masterProductId, freshQuote);
        }
    }
    async updateQuoteLineItem(payload = {}) {
        const { id, product } = payload as {
            id: number;
            product?: Record<string, any>;
        };

        if (id) {
            this.setLineItemLoading(id);
        }

        const isFileUpdate = !!product && ('file_2d' in product || 'file3d' in product || 'file_3d' in product);

        const is2dDelete =
            !!product && Object.prototype.hasOwnProperty.call(product, 'file_2d') && product.file_2d === null;

        return patch(`/customer/quote/${this.getQuoteUid}/update-item/`, payload)
            .then(async data => {
                this.setQuoteLineItem(data);
                this.maybeQueueEmbeddedExtraction(data);

                const masterProductId = data?.product?.master_product;

                if (isFileUpdate && masterProductId && this.getQuoteUid) {
                    const freshQuote = await this.getQuoteByUid(this.getQuoteUid, {
                        updateLineItems: false,
                        useSummaryLoading: true,
                    });

                    this.refreshMasterProductGroupFromQuote(masterProductId, freshQuote);
                }

                if (is2dDelete && data?.manufacturing_type === 'CNC' && masterProductId) {
                    await this.repriceSiblingVariationsAfter2dDelete(data.id, masterProductId);
                }

                return data;
            })
            .finally(() => {
                this.setLineItemLoading(null);
            });
    }

    get masterProductsMap() {
        if (!this?.line_items) return new Map<number, QuoteLineItemProps[]>();

        const map = new Map<number, QuoteLineItemProps[]>();

        for (const item of this?.line_items) {
            const key = item.product.master_product;
            const list = map.get(key);

            if (list) {
                list.push(item);
            } else {
                map.set(key, [item]);
            }
        }

        return map;
    }

    get sortedMasterProducts() {
        // Keep groups in original order (Map insertion order),
        // only sort items within groups when there are variations.
        return [...this.masterProductsMap.values()].map(group => {
            if (group.length <= 1) return group;
            return [...group].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        });
    }

    /***
     * Use this option to update a field in the quote
     * @param key
     * @param value
     */
    async updateQuoteField(key: string, value: any) {
        if (this.quote) {
            const payload = {
                [key]: value,
            };
            this.setQuoteSummaryLoading(true);
            this.setQuoteLoading(true);

            await patch(`/customer/quote/${this.getQuoteUid}/`, payload)
                .then(data => {
                    runInAction(() => {
                        this.quote = data;
                        this.line_items = data.line_items;
                    });
                    return data;
                })
                .finally(() => {
                    this.setQuoteSummaryLoading(false);
                    this.setQuoteLoading(false);
                });
        }
    }

    /***
     * Update multiple quote fields in a single request (same endpoint as updateQuoteField).
     */
    async updateQuoteFields(payload: Record<string, any>) {
        if (this.quote && Object.keys(payload).length > 0) {
            this.setQuoteSummaryLoading(true);
            await patch(`/customer/quote/${this.getQuoteUid}/`, payload)
                .then(data => {
                    runInAction(() => {
                        if (this.quote) {
                            this.quote = data;
                            this.line_items = this.quote?.line_items ?? [];
                        }
                    });
                    return data;
                })
                .finally(() => {
                    this.setQuoteSummaryLoading(false);
                });
        }
    }

    removeLineItemCoatings(id: number, payload = {}) {
        return post(`/customer/quotes/${id}/remove-coatings/`, payload);
    }

    async getPricesLineItem(id: number, payload = {}) {
        runInAction(() => {
            this.loading = true;
        });

        return await post(`/customer/quotes/${id}/get-cost/`, payload)
            .then(data => {
                runInAction(() => {
                    this.line_item = data.instance;
                    this.show_prices = data.show_prices;
                });
            })
            .catch(err => {
                message.error('Error, please try again');
            })
            .finally(() => {
                this.setLoading(false);
            });
    }

    sendToReviewBySignedUid(signedUid: string, payload: any = {}) {
        return post(`/customer/quote/${signedUid}/send-to-review/`, payload);
    }

    sendToReview(payload = {}) {
        this.setQuoteLoading(true);
        this.setQuoteSummaryLoading(true);
        return post(`/customer/quote/${this.getQuoteUid}/send-to-review/`, payload)
            .then(response => {
                runInAction(() => {
                    this.quote = null;
                });

                return response;
            })
            .finally(() => {
                this.setQuoteLoading(false);
                this.setQuoteSummaryLoading(false);
            });
    }

    set_line_item(line_item?: QuoteLineItemProps) {
        runInAction(() => {
            if (line_item) {
                this.line_item = line_item;
            } else {
                this.line_item = undefined;
                this.show_prices = true;
            }
        });
    }

    get errorFields() {
        const stepsIds = { process: 0, material: 1, finishes: 2, coatings: 3 };
        if (this.quote && this.quote.line_items) {
            return this.quote.line_items?.reduce((acc: Array<ErrorField>, item: QuoteLineItemProps) => {
                const fieldName = (name: string) => {
                    return name + '__' + item.id;
                };

                if (!item?.product?.material) {
                    acc.push({
                        step: stepsIds.material,
                        fieldName: fieldName('material'),
                        lineItem: String(item.id),
                    });
                } else {
                    if (item?.product?.material === 'Custom' && !item?.product?.custom_material) {
                        acc.push({
                            step: stepsIds.material,
                            fieldName: fieldName('custom_material'),
                            lineItem: String(item.id),
                        });
                    }
                }

                if (item?.product?.finishes?.length === 0) {
                    acc.push({
                        step: stepsIds.finishes,
                        fieldName: fieldName('finishes'),
                        lineItem: String(item.id),
                    });
                } else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (item?.product?.finishes[0] === 'Custom' && !item?.product?.custom_finish) {
                        acc.push({
                            step: stepsIds.finishes,
                            fieldName: fieldName('custom_finish'),
                            lineItem: String(item.id),
                        });
                    }
                }

                if (!item?.coating) {
                    acc.push({
                        step: stepsIds.coatings,
                        fieldName: fieldName('coatings'),
                        lineItem: String(item.id),
                    });
                }

                if (item?.product?.coatings?.length === 0) {
                    acc.push({
                        step: stepsIds.coatings,
                        fieldName: fieldName('coatings'),
                        lineItem: String(item.id),
                    });
                } else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (item?.product?.coatings[0] === 'Custom' && !item?.product?.custom_coating) {
                        acc.push({
                            step: stepsIds.coatings,
                            fieldName: fieldName('custom_coating'),
                            lineItem: String(item.id),
                        });
                    }
                }

                return acc;
            }, []);
        } else {
            return [];
        }
    }

    get getQuoteLineItems() {
        return this.line_items;
    }

    async loadParentLineItems(quote_id: number) {
        const data = await get(`/customer/quotes/${quote_id}/line-items/`);

        runInAction(() => {
            this.parent_line_items = data.results;
        });
    }

    get filterParentLineItems() {
        return _(this.parent_line_items)
            .differenceBy(this.line_items, 'name')
            .map(_.partial(_.pick, _, 'id', 'name', 'product'))
            .value();
    }

    remove_line_items(_item: any, _quantity_item?: any) {
        runInAction(() => {
            if (_quantity_item) {
                this.line_items.map(item => {
                    if (item.id === _item.id) {
                        item.quote_item_quantities.splice(
                            item.quote_item_quantities.findIndex(
                                (dynamic: { id: any }) => dynamic.id === _quantity_item.id
                            ),
                            1
                        );
                        if (item.has_dynamic_quantities) {
                            const dynamics = item.quote_item_quantities.filter(
                                (dynamic: { displayed: boolean }) => dynamic.displayed
                            );
                            if (dynamics.length === 0) {
                                this.line_items.splice(
                                    this.line_items.findIndex((_item: { id: number }) => _item.id === item.id),
                                    1
                                );
                                this.deleteLineItem(_item.quote, { id: _item.id });
                            }
                        }
                    }
                });
            } else {
                this.line_items = this.line_items.filter(item => item.id !== _item.id);
            }
        });
    }

    update_prices_line_item(_item_id: number, prices: any, _quantity_item_id?: number) {
        runInAction(() => {
            this.line_items = this.line_items.map(item => {
                if (_item_id === item.id) {
                    item.quantity = prices.quantity;
                    item.lead_time = prices.lead_time;
                    item.unit_price = prices.unit_price;
                    item.total_price = prices.total_price;
                }
                if (_item_id === item.id && _quantity_item_id && prices) {
                    item.quote_item_quantities.map(
                        (quantity_item: {
                            id: number;
                            quantity: number;
                            lead_time: number;
                            unit_price: string;
                            total_price: string;
                        }) => {
                            if (quantity_item.id === _quantity_item_id) {
                                quantity_item.quantity = prices.quantity;
                                quantity_item.lead_time = prices.lead_time;
                                quantity_item.unit_price = prices.unit_price;
                                quantity_item.total_price = prices.total_price;
                            }
                        }
                    );
                }
                return item;
            });
        });

        return this.line_items;
    }

    getQuoteLineItemsByMasterId = async (params: any) => {
        return await get(`/customer/quote-line-items/`, { params: params });
    };

    requote(id: number) {
        return requests.post(`/customer/quotes/${id}/requote/`);
    }

    /**
     * Creates a new quote revision (PDF “Order Now”, quote detail header, etc.).
     * Centralized here for shared use and future MobX state around revisions.
     */
    createQuoteNewRevision(quoteId: number): Promise<QuoteNewRevisionResponse> {
        return post(`/customer/quotes/${quoteId}/new-revision/`, {});
    }

    setSelectedLineItems(payload: QuoteLineItemProps) {
        this.selectedLineItems = [...this.selectedLineItems, payload];
    }

    cleanSelectedLineItems() {
        this.selectedLineItems = [];
    }

    removeSelectedLineItems(quote_index: number, quote_id: number) {
        const indexToRemove = this.selectedLineItems.findIndex(
            (item, index) => item.id === quote_id && index === quote_index
        );

        if (indexToRemove !== -1) {
            this.selectedLineItems.splice(indexToRemove, 1);
        }
    }

    /**
     * Fetches quote data from the API.
     * @param uid - Quote UID
     * @param options - Configuration options
     * @param options.updateLineItems - Whether to update this.line_items (default: true)
     *   Set to false to avoid triggering coating sync reactions when only quote metadata changed
     * @param options.useSummaryLoading - Whether to use quoteSummaryLoading state (default: false)
     *   Set to true for lightweight metadata refreshes
     */
    async getQuoteByUid(uid: string, options: { updateLineItems?: boolean; useSummaryLoading?: boolean } = {}) {
        const { updateLineItems = true, useSummaryLoading = false } = options;

        if (useSummaryLoading) {
            this.setQuoteSummaryLoading(true);
        } else {
            this.setQuoteLoading(true);
        }

        return await get(`/customer/quote/${uid}/`)
            .then(data => {
                runInAction(() => {
                    this.quote = data;
                    if (updateLineItems) {
                        this.line_items = data.line_items;
                    }
                });

                return data;
            })
            .catch(err => {
                throw err;
            })
            .finally(() => {
                if (useSummaryLoading) {
                    this.setQuoteSummaryLoading(false);
                } else {
                    this.setQuoteLoading(false);
                }
            });
    }

    getQuoteBySignedUid(signedUid: string) {
        // signedUid is "uid:token"
        return get(`/customer/quote/${signedUid}/`);
    }

    async updateItemPrice(quote_id: string | number, payload = {}) {
        if (quote_id) {
            const response = await patch(`/customer/quote/${quote_id}/update-item-price/`, payload).then(data => {
                this.setQuoteLineItem(data);

                return data;
            });

            return response;
        }
    }

    async validateOwnerQuoteByUid(uid: string) {
        return await get(`/customer/quote/validate-owner/?uid=${uid}`)
            .then(data => {
                return data;
            })
            .catch(error => {
                throw error;
            });
    }

    get isSafeToCheckout(): boolean {
        const items = this.line_items;
        if (!Array.isArray(items) || items.length === 0) return false;

        return items.every(
            item => item?.safe_auto_quote || (item?.needs_manual_quote && !item?.is_original_item_auto_safe)
        );
    }

    setQuoteUid(uid: string) {
        this.quoteUid = uid;
    }

    async getLastQuoteByEmail(email: string) {
        if (this.quote && this.line_items.length > 0 && this.quote?.type !== 'Requote') {
            return;
        }

        this.setQuoteLoading(true);

        try {
            const data = await get(`/customer/quote/last/?email=${encodeURIComponent(email)}`);
            runInAction(() => {
                this.quote = data;
                this.line_items = data.line_items;
                this.quoteUid = data.uid;
            });
            return data;
        } finally {
            this.setQuoteLoading(false);
        }
    }

    async getLastDraftQuoteById(id: string) {
        this.setQuoteLoading(true);

        try {
            const data = await get(`/customer/quote/last/?draft_id=${id}`);
            runInAction(() => {
                this.quote = data;
                this.line_items = data.line_items;
                this.quoteUid = data.uid;
            });
            return data;
        } finally {
            this.setQuoteLoading(false);
        }
    }

    async deleteMasterProduct(master_product_id: number) {
        await remove(`/customer/quote/${this.getQuoteUid}/remove-part/${master_product_id}/`)
            .then(data => {
                runInAction(() => {
                    this.line_items = data.line_items;
                });

                return data;
            })
            .catch(err => {
                return err;
            });
    }

    async removeVariation(item_uid: string) {
        runInAction(() => {
            this.loading = true;
        });

        return await remove(`/customer/quote/${this.getQuoteUid}/remove-item/${item_uid}/`)
            .then(data => {
                runInAction(() => {
                    this.line_items = data.line_items;
                });

                return data;
            })
            .catch(err => {
                message.error('Error, please try again');
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async setQuoteLineItem(data: QuoteLineItemProps) {
        if (!this.line_items) return null;

        runInAction(() => {
            // Use map to create a new array with updated item
            // This preserves order and creates new references for better reactivity
            const newLineItems = this.line_items.map(item => {
                // Match by id or uid to handle both cases
                if (item.id === data.id || item.uid === data.uid) {
                    return {
                        ...item,
                        ...data,
                    };
                }
                return item;
            });

            this.line_items = newLineItems;

            // // Also update the quote's line_items if it exists to keep them in sync
            if (this.quote) {
                this.quote.line_items = this.line_items;
            }
        });

        return this.line_items;
    }

    enqueuePartUploads(items: IPartFileRecord[]) {
        // Fixed now: the quote and the slot the parts go to, whatever the user opens meanwhile.
        const quoteUid = this.getQuoteUid;
        // Never queue an upload without a quote (PTB-7157): a guest who dismisses
        // the sign-up modal has none yet, and a logged-in visit whose quote failed
        // to load has none either. The upload could only fail after polling for a
        // UID. The parts stay pending in fileParts and are queued once a quote
        // exists (useQuotePartsHandlers re-runs when it appears).
        if (!quoteUid) return;
        const existingUids = new Set(
            this.backgroundUploadJobs
                .filter(job => job.status === 'queued' || job.status === 'uploading')
                .map(job => job.uid)
        );
        const email = this.quote?.email ?? null;
        const nextPosition = (this.line_items?.length ?? 0) + existingUids.size + 1;
        const jobs = items
            .filter(item => (item['3D File'] || item['2D File']) && !existingUids.has(item.uid))
            .map(
                (item, index): BackgroundUploadJob => {
                    const names = [item['3D File']?.name, item['2D File']?.name].filter(Boolean);
                    return {
                        uid: item.uid,
                        item,
                        position: nextPosition + index,
                        label: names.join(' + ') || `Part ${index + 1}`,
                        status: 'queued',
                        progress: 0,
                        quoteUid,
                        email,
                    };
                }
            );

        if (!jobs.length) return;

        runInAction(() => {
            this.backgroundUploadJobs = [...this.backgroundUploadJobs, ...jobs];
            this.backgroundUploadDismissed = false;
            // Rows without a file (a removed or moved file) would stay behind as ghosts.
            this.fileParts = this.fileParts.filter(
                item => !jobs.some(job => job.uid === item.uid) && (item['3D File'] || item['2D File'])
            );
        });

        void this.processBackgroundUploadQueue();
    }

    async processBackgroundUploadQueue() {
        if (this.backgroundUploadRunning) return;

        this.backgroundUploadRunning = true;
        try {
            let job = this.backgroundUploadJobs.find(candidate => candidate.status === 'queued');
            while (job) {
                runInAction(() => {
                    job!.status = 'uploading';
                    job!.progress = 0;
                    job!.error = undefined;
                });

                try {
                    await (this.backgroundUploadExecutor || this.uploadPart)(job.item, job.position, job);
                    runInAction(() => {
                        job!.status = 'completed';
                        job!.progress = 100;
                    });
                } catch (error) {
                    runInAction(() => {
                        job!.status = 'failed';
                        job!.error = describeUploadError(error);
                    });
                }

                job = this.backgroundUploadJobs.find(candidate => candidate.status === 'queued');
            }
        } finally {
            runInAction(() => {
                this.backgroundUploadRunning = false;
            });
        }
    }

    retryFailedBackgroundUploads() {
        runInAction(() => {
            this.backgroundUploadJobs.forEach(job => {
                if (job.status === 'failed') {
                    job.status = 'queued';
                    job.progress = 0;
                    job.error = undefined;
                }
            });
            this.backgroundUploadDismissed = false;
        });
        void this.processBackgroundUploadQueue();
    }

    dismissBackgroundUploads() {
        if (this.backgroundUploadJobs.some(job => job.status === 'queued' || job.status === 'uploading')) return;
        runInAction(() => {
            this.backgroundUploadJobs = [];
            this.backgroundUploadDismissed = true;
        });
    }

    private updateBackgroundUploadProgress(uid: string, progress: number) {
        const now = Date.now();
        const lastUpdated = this.uploadProgressUpdatedAt.get(uid) || 0;
        if (progress < 100 && now - lastUpdated < 200) return;

        this.uploadProgressUpdatedAt.set(uid, now);
        const job = this.backgroundUploadJobs.find(candidate => candidate.uid === uid);
        if (job) {
            runInAction(() => {
                job.progress = progress;
            });
        }
    }

    async uploadPart(item: IPartFileRecord, position = 1, job?: BackgroundUploadJob) {
        const file3d = item['3D File'];

        const formData = new FormData();

        if (file3d) {
            formData.append('file_3d', file3d);
        }

        if (item['2D File']) {
            formData.append('file_2d', item['2D File']);
        }

        // if (item['Additional File']?.length) {
        //     item['Additional File'].map((f: File) => formData.append('file_others', f));
        // }

        // Preserve the row position captured before completed jobs leave the queue.
        formData.append('position', position.toString());

        const email = job ? job.email : this.quote?.email;
        if (email) {
            formData.append('email', email);
        }

        const onUploadProgress = (progressEvent: any) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            this.updateBackgroundUploadProgress(item.uid, Number(progress?.toFixed(0)) || 0);
        };

        let data: any;
        if (job?.lineItemId) {
            // A retry after the part was created: do not create it again.
            data = this.line_items?.find(lineItem => lineItem.id === job.lineItemId) ?? { id: job.lineItemId };
        } else {
            data = await this.newLineItemWithFiles(formData, onUploadProgress, job?.quoteUid ?? undefined);
            if (job && data?.id) {
                runInAction(() => {
                    job.lineItemId = data.id;
                });
            }
        }

        if (file3d && isStlFile(file3d.name) && data?.id && data.manufacturing_type !== STL_MANUFACTURING_TYPE) {
            const payload = buildManufacturingTypePayloadForStlPart(data, STL_MANUFACTURING_TYPE);
            if (job?.quoteUid && job.quoteUid !== this.getQuoteUid) {
                // The part belongs to a quote the user left: update it there, not on the form on screen.
                await patch(`/customer/quote/${job.quoteUid}/update-item/`, payload);
            } else {
                await this.updateQuoteLineItem(payload);
            }
        }
    }

    /** Add a dropped 3D or 2D file and pair it with the other files by name. */
    addPart = (file: File) => {
        const hasExtension = file.name.includes('.');
        const isAdditionalFileRestrictedForHobbyist =
            this?.quote?.contact?.is_hobbyist && !is3dFile(file.name) && !is2dFile(file.name);
        const fileExtension = hasExtension ? file.name.split('.').pop()?.toLowerCase() : undefined;

        if (
            isAdditionalFileRestrictedForHobbyist ||
            !hasExtension ||
            (fileExtension && restricted_file_extensions.includes(fileExtension))
        ) {
            message.warning(
                fileExtension
                    ? `.${fileExtension} files are not allowed. Please try another file type.`
                    : `Files without an extension are not allowed. Please try another file type.`,
                4
            );
            return;
        }

        // Exclude Additional Files
        if (!is3dFile(file.name) && !is2dFile(file.name)) {
            message.warning(`Only 3D and 2D files are allowed. ${file.name} will not be added.`, 4);
            return;
        }

        this.arrivalOrder.set(file, this.nextArrival++);
        this.rematchParts(file);
    };

    /** Order in which files were added, so new parts appear in drop order. */
    private arrivalOrder = new WeakMap<File, number>();
    private nextArrival = 0;

    /**
     * Re-pair every automatically matched 3D and 2D file together with the new one, so the
     * result does not depend on the order the files arrive in. Parts the user edited stay as
     * they are, parts keep their place in the list, and a part keeps its uid while its 3D
     * (or, without one, its 2D) file stays in it.
     */
    private rematchParts(added: File) {
        const auto = this.fileParts.filter(part => !part.locked && !part.uploading);
        const files = [
            ...auto.flatMap(part => [part['3D File'], part['2D File']]),
            added,
        ].filter((file): file is File => Boolean(file));
        const files3d = files.filter(file => is3dFile(file.name));
        const files2d = files.filter(file => !is3dFile(file.name));

        const pairs = matchPartFiles(files3d, files2d, file => file.name);
        const paired = new Set(pairs.flatMap(pair => [pair.file3d, pair.file2d]));
        const groups: Array<{ '3D File': File | null; '2D File': File | null; similarity: number }> = [
            ...pairs.map(pair => ({ '3D File': pair.file3d, '2D File': pair.file2d, similarity: pair.score })),
            ...files3d
                .filter(file => !paired.has(file))
                .map(file => ({ '3D File': file, '2D File': null, similarity: 0 })),
            ...files2d
                .filter(file => !paired.has(file))
                .map(file => ({ '3D File': null, '2D File': file, similarity: 0 })),
        ];

        const placeOf = new Map<File, number>();
        this.fileParts.forEach((part, index) => {
            [part['3D File'], part['2D File']].forEach(file => file && placeOf.set(file, index));
        });
        const place = (file: File | null) =>
            !file ? Infinity : placeOf.get(file) ?? this.fileParts.length + (this.arrivalOrder.get(file) ?? 0);

        const usedUids = new Set<string>();
        const previousPart = (file: File | null) =>
            file ? auto.find(part => part['3D File'] === file || part['2D File'] === file) : undefined;
        const rebuilt = groups.map(group => {
            const previous = [previousPart(group['3D File']), previousPart(group['2D File'])].find(
                part => part && !usedUids.has(part.uid)
            );
            const uid = previous?.uid ?? uuidv4();
            usedUids.add(uid);
            return { ...(previous ?? {}), ...group, uid, uploading: false, progress: 0 } as IPartFileRecord;
        });

        const ranked = [
            ...this.fileParts
                .filter(part => !auto.includes(part))
                .map(part => ({ part, rank: this.fileParts.indexOf(part) })),
            ...rebuilt.map(part => ({ part, rank: Math.min(place(part['3D File']), place(part['2D File'])) })),
        ];
        this.setParts(ranked.sort((a, b) => a.rank - b.rank).map(({ part }) => part));
    }

    removePart(item: IPartFileRecord) {
        this.setParts([...this.fileParts.filter(i => i.uid !== item.uid)]);
    }

    cleanAllParts() {
        this.setParts([]);
    }

    removeFile(item: IPartFileRecord, fileType: '3D File' | '2D File', file?: File) {
        this.setParts(this.fileParts.map(i => (i.uid === item.uid ? { ...i, [fileType]: null } : i)));
    }

    addFile(index: number, fileType: '3D File' | '2D File', file: File) {
        runInAction(() => {
            this.fileParts[index][fileType] = file;
            this.fileParts[index].locked = true;
        });
    }

    /**
     * Swap a part file
     * @param fromIndex: index from the file was picked
     * @param toIndex: index where the file was dropped
     * @param fromType: Spot file type where the file was picker
     * @param toType: Spot File type were the file is dropped
     */
    swapParts(
        index_from: number,
        index_to: number,
        FileTypeFrom: '3D File' | '2D File' | 'Additional File',
        FileTypeTo: '3D File' | '2D File' | 'Additional File'
    ) {
        const fromPart = this.fileParts[index_from];
        const toPart = this.fileParts[index_to];

        if (!fromPart || !toPart) {
            return;
        }

        const file_from = fromPart?.[FileTypeFrom];
        const file_to = toPart?.[FileTypeTo];

        this.setParts(
            this.fileParts.map((part, index) => {
                if (index === index_from) {
                    return {
                        ...part,
                        [FileTypeFrom]: file_to,
                        locked: true,
                    };
                }

                if (index === index_to) {
                    return {
                        ...part,
                        [FileTypeTo]: file_from,
                        locked: true,
                    };
                }

                return part;
            })
        );
    }

    setParts(parts: Array<IPartFileRecord>) {
        runInAction(() => {
            this.fileParts = parts;
        });
    }

    private async requireQuoteUid(timeoutMs = 10000): Promise<string> {
        const startedAt = Date.now();

        while (Date.now() - startedAt < timeoutMs) {
            const uid = this.getQuoteUid;
            if (uid) return uid;

            const token = window.localStorage.getItem('quote_token');
            const isAuthenticated = Boolean(window.localStorage.getItem('token'));
            if (token && !isAuthenticated) {
                // Keep store in sync with persisted token so subsequent calls succeed.
                if (!this.rapidQuoteUid) {
                    this.setRapidQuoteUid(token);
                }
                return token;
            }

            await new Promise(resolve => setTimeout(resolve, 50));
        }

        throw new Error('Quote UID not available (quote start still in progress).');
    }

    async newLineItemWithFiles(payload = {}, onUploadProgress: any, quoteUid?: string) {
        const options = {
            onUploadProgress: onUploadProgress && onUploadProgress,
        };
        const targetUid = quoteUid ?? (await this.requireQuoteUid());

        return await post(`/customer/quote/${targetUid}/new-item/`, payload, options, true)
            .then(data => {
                runInAction(() => {
                    // Only the quote on screen takes the new line; a queued upload for a quote the
                    // user left is saved on the server and shows up when that quote is opened.
                    if (this.getQuoteUid === targetUid || this.quote?.uid === targetUid) {
                        this.line_items?.push(data);
                        if (this.quote) this.quote.line_items = this.line_items;
                    }
                });
                this.maybeQueueEmbeddedExtraction(data);
                // Do not auto-expand the new part (avoids modal opening on mobile after file drop)
                return data;
            })
            .catch(err => {
                console.log(err);
                trackLuckyOrangeEvent('File Upload Error', {
                    quote_id: targetUid,
                    page: typeof window !== 'undefined' ? window.location.pathname : undefined,
                });
                throw err;
            });
    }

    cleanRapidQuoteData() {
        this.setRapidQuoteUid(null);
        // Key that we want to keep in localStorage
        Object.keys(localStorage).forEach(key => {
            // If the key is not the one we want to keep, remove it
            if (key !== 'utm_params') {
                window.localStorage.removeItem(key);
            }
        });
    }

    get getQuoteUid() {
        if (this.quoteUid) {
            return this.quoteUid;
        }

        const isAuthenticated = typeof window !== 'undefined' && Boolean(window.localStorage.getItem('token'));

        // Logged-in users must use the plain quote UUID. The API only unsigns uids for guests.
        if (isAuthenticated) {
            return this.quote?.uid ?? null;
        }

        if (this.rapidQuoteUid) {
            return this.rapidQuoteUid;
        }

        if (this.quote?.uid) {
            return this.quote.uid;
        }

        return null;
    }

    get getZipCode() {
        // Prefer the quote's own shipping_zip_code (user-editable) over the
        // contact's stored zip so that a zip entered in the form is not masked
        // by the contact's historic zip on re-render or page refresh.
        return this.quote?.shipping_zip_code || this.quote?.contact?.zip_code || '';
    }

    get areAllItemsSafeToAutoQuote(): boolean {
        if (!this.quote) return false;

        return this.quote.line_items.every(item => item.safe_auto_quote && !item.needs_manual_quote);
    }

    async cleanQuote() {
        runInAction(() => {
            this.quote = null;
            this.line_items = [];
            // Finished jobs (and their File blobs) belong to the quote being left.
            this.backgroundUploadJobs = this.backgroundUploadJobs.filter(
                job => job.status === 'queued' || job.status === 'uploading'
            );
        });
    }
}

const quoteStore = new QuoteStore();

export default quoteStore;
